import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useContactProjectsListDataTable,
} from 'generated/resources/datatable.graphql'
import compact from 'lodash/compact'

import { formatBackendDateToDayMonthYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/project_management/components/Amount'
import {
  BudgetProgressBar,
  EInfoLabelLocation,
} from 'modules/project_management/components/BudgetProgressBar'
import BillableTag from 'modules/project_management/components/labelTags/BillableTag'
import ProjectStatusTag from 'modules/project_management/components/labelTags/StatusTag'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import {
  getFixedCostPlanningProgressBarLabel,
  getFixedTimeBudgetProgressBarLabel,
} from 'modules/project_management/lib/formatText'

import { Actions } from './Actions'
import ProjectWorkers from './TableBody/ProjectWorkers'

// This type will be automatically generated by the plugin, so we will be able to import it from the generated file
type ClientProjectsListDataTableProjections = Parameters<
  typeof useContactProjectsListDataTable
>[0][number]

const projectsDatatableCostAccumulation = (
  fixedCost: number | undefined,
  laborCost: undefined,
  spendingCost: undefined
) => {
  return Number(fixedCost) || 0 + Number(laborCost) || 0 + Number(spendingCost) || 0
}

const ProjectNameProjection = (): ClientProjectsListDataTableProjections => {
  return {
    kind: ProjectionKind.Custom,
    identifier: 'name',
    title: i18n.t('project_management.table.projects_list.headers.name'),
    template: '{{name}}',
    width: 200,
    colSpan: 3,
    render: ({ name }) => {
      return (
        <Box minWidth="s200">
          <Text.Normal overflowWrap>{name}</Text.Normal>
        </Box>
      )
    },
  }
}

const ProjectCodeProjection = (): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  identifier: 'code',
  title: i18n.t('project_management.table.projects_list.headers.code'),
  template: '{{code}}',
  width: 120,
  colSpan: 3,
  sortBy: 'code',
  render: ({ code }) => {
    return (
      <Box minWidth="s120">
        <Text.Normal overflowWrap>{code || '-'}</Text.Normal>
      </Box>
    )
  },
})

const ProjectWorkersProjection = (): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  identifier: 'projectWorkers',
  title: i18n.t('project_management.table.projects_list.headers.employees'),
  render: ({ projectworkersConnection }) => {
    const totalCount = projectworkersConnection.totalCount
    const employees = compact(projectworkersConnection?.edges.map((edge) => edge.node.employee))

    return totalCount && employees.length ? (
      <ProjectWorkers employees={employees} totalCount={totalCount} />
    ) : (
      '-'
    )
  },
  template: '{{projectWorkers}}',
  colSpan: 3,
})

const ProjectHoursProjection = ({
  hasFixedTimeBudget,
}: {
  hasFixedTimeBudget: boolean
}): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  identifier: 'hours',
  title: i18n.t('project_management.table.projects_list.headers.hours'),
  render: ({ inputedMinutes, fixedTimeBudget }) => (
    <Box minWidth="s80">
      {hasFixedTimeBudget && fixedTimeBudget?.budgetAllocation ? (
        <Box width="s140">
          <BudgetProgressBar
            currentAmount={inputedMinutes || 0}
            totalAmount={fixedTimeBudget?.budgetAllocation}
            infoLabelLocation={EInfoLabelLocation.TOOLTIP}
            infoLabel={getFixedTimeBudgetProgressBarLabel(
              formatProjectHours(inputedMinutes),
              formatProjectHours(fixedTimeBudget?.budgetAllocation)
            )}
          />
        </Box>
      ) : (
        <Text.Normal>{formatProjectHours(inputedMinutes)}</Text.Normal>
      )}
    </Box>
  ),
  template: '{{hours}}',
  colSpan: 3,
})

const ProjectStartDateProjection = (): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  title: i18n.t('project_management.table.projects_list.headers.startDate'),
  identifier: 'startDate',
  template: '{{startDate}}',
  render: ({ startDate }) => (
    <Box minWidth="s120">
      <Text.Normal>{(startDate && formatBackendDateToDayMonthYear(startDate)) || '-'}</Text.Normal>
    </Box>
  ),
  width: 120,
  colSpan: 3,
  sortBy: 'startDate',
})

const ProjectDueDateProjection = (): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  title: i18n.t('project_management.table.projects_list.headers.dueDate'),
  identifier: 'dueDate',
  template: '{{dueDate}}',
  render: ({ dueDate }) => (
    <Box minWidth="s120">
      <Text.Normal>{(dueDate && formatBackendDateToDayMonthYear(dueDate)) || '-'}</Text.Normal>
    </Box>
  ),
  width: 120,
  colSpan: 3,
  sortBy: 'dueDate',
})

const ProjectIsBillableProjection = (): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  identifier: 'isBillable',
  title: i18n.t('project_management.table.projects_list.headers.billing'),
  render: ({ isBillable }) => (
    <Box minWidth="s120">
      <BillableTag isBillable={!!isBillable} />
    </Box>
  ),
  template: '{{isBillable}}',
  sortBy: 'isBillable',
  colSpan: 3,
})

const ProjectCostProjection = (locale: string): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  identifier: 'cost',
  title: i18n.t('project_management.table.projects_list.headers.cost'),
  render: ({
    fixedCostCents,
    laborCostCents,
    fixedCostPlanning,
    legalEntity_currency,
    spendingCostCents,
  }) => {
    const currency = legalEntity_currency
    const fixedCostNumber = Number(fixedCostCents) || 0
    const cost = projectsDatatableCostAccumulation(
      fixedCostNumber,
      laborCostCents,
      spendingCostCents
    )

    return (
      <Box minWidth="s120">
        {fixedCostPlanning?.budgetAllocationCents ? (
          <Box width="s140">
            <BudgetProgressBar
              currentAmount={fixedCostNumber}
              totalAmount={fixedCostPlanning.budgetAllocationCents}
              infoLabelLocation={EInfoLabelLocation.TOOLTIP}
              infoLabel={getFixedCostPlanningProgressBarLabel(
                fixedCostNumber,
                fixedCostPlanning.budgetAllocationCents,
                currency,
                locale
              )}
            />
          </Box>
        ) : (
          <Amount amount={cost} currency={currency} />
        )}
      </Box>
    )
  },
  template: '{{cost}}',
  colSpan: 3,
})

const ProjectStatusProjection = (): ClientProjectsListDataTableProjections => ({
  kind: ProjectionKind.Custom,
  identifier: 'status',
  title: i18n.t('project_management.table.projects_list.headers.status'),
  render: ({ status }) => status && <ProjectStatusTag projectStatus={status} />,
  template: '{{status}}',
  colSpan: 3,
  sortBy: 'status',
})

const ProjectActionsProjection = (
  removeProject: (projectId: number) => void,
  canManageProjects: boolean
): ClientProjectsListDataTableProjections => {
  return {
    identifier: 'actions',
    kind: ProjectionKind.Decorative,
    render: ({ id }) => {
      if (!canManageProjects) return null

      return <Actions id={id} removeProject={removeProject} />
    },
    fixed: true,
    sticky: true,
  }
}

export const clientProjectsListProjections = ({
  locale,
  hasBillable,
  hasFixedTimeBudget,
  hasCost,
  canManageProjects,
  removeProject,
}: {
  locale: string
  hasBillable: boolean
  hasFixedTimeBudget: boolean
  hasCost: boolean
  canManageProjects: boolean
  removeProject: (projectId: number) => void
}) =>
  compact([
    ProjectNameProjection(),
    ProjectCodeProjection(),
    ProjectWorkersProjection(),
    ProjectHoursProjection({ hasFixedTimeBudget }),
    hasBillable ? ProjectIsBillableProjection() : null,
    hasCost ? ProjectCostProjection(locale) : null,
    ProjectStartDateProjection(),
    ProjectDueDateProjection(),
    ProjectStatusProjection(),
    ProjectActionsProjection(removeProject, canManageProjects),
  ])
